// Build the context payload sent to /api/chat. The chat backend is told to
// answer only from these numbered sources, so the quality of selection here
// directly governs hallucination risk.

import type { ChapterModule } from "@/data/chapters/types";

export interface ChatSource {
  id: string;
  kind: "glossary" | "concept" | "excerpt" | "example" | "procedure" | "comparison" | "misconception" | "overview";
  chapterId: string;
  chapterTitle: string;
  label: string;
  text: string;
  anchor?: string;
  page?: number;
}

export interface ChatContextPayload {
  chapterId?: string;
  chapterTitle?: string;
  question: string;
  history: { role: "user" | "assistant"; content: string }[];
  sources: ChatSource[];
}

const MAX_GLOSSARY = 8;
const MAX_CONCEPTS = 4;
const MAX_EXCERPTS = 4;
const MAX_EXAMPLES = 3;
const MAX_PROCEDURES = 2;
const MAX_COMPARISONS = 2;
const MAX_MISCONCEPTIONS = 2;

function tokens(s: string): string[] {
  return s.toLowerCase().match(/[a-z0-9]+/g) ?? [];
}

function score(haystack: string, needles: string[]): number {
  if (needles.length === 0) return 0;
  const h = haystack.toLowerCase();
  let n = 0;
  for (const t of needles) if (h.includes(t)) n += 1;
  return n;
}

function pickTop<T>(items: T[], score: (x: T) => number, n: number): T[] {
  return items
    .map(x => ({ x, s: score(x) }))
    .filter(({ s }) => s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, n)
    .map(({ x }) => x);
}

function truncate(s: string, max = 480): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "...";
}

let nextId = 1;
function srcId(): string {
  return `SRC-${nextId++}`;
}

export function buildSingleChapterContext(
  chapter: ChapterModule,
  question: string,
  history: ChatContextPayload["history"],
): ChatContextPayload {
  nextId = 1;
  const q = tokens(question);
  const sources: ChatSource[] = [];

  const topGlossary = pickTop(chapter.glossary, g => score(`${g.term} ${g.definition}`, q) * 2 + 1, MAX_GLOSSARY);
  for (const g of topGlossary) {
    sources.push({
      id: srcId(),
      kind: "glossary",
      chapterId: chapter.meta.id,
      chapterTitle: chapter.meta.title,
      label: g.term,
      text: truncate(g.definition),
      anchor: "glossary",
    });
  }

  const topConcepts = pickTop(chapter.coreConcepts, c => score(`${c.title} ${c.body.join(" ")}`, q) * 2, MAX_CONCEPTS);
  for (const c of topConcepts) {
    sources.push({
      id: srcId(),
      kind: "concept",
      chapterId: chapter.meta.id,
      chapterTitle: chapter.meta.title,
      label: c.title,
      text: truncate(c.body.join(" ")),
      anchor: "concepts",
    });
  }

  if (chapter.sourceExcerpts && chapter.sourceExcerpts.length > 0) {
    const topExcerpts = pickTop(chapter.sourceExcerpts, ex => score(`${ex.heading} ${ex.text}`, q) * 3 + 1, MAX_EXCERPTS);
    for (const ex of topExcerpts) {
      sources.push({
        id: srcId(),
        kind: "excerpt",
        chapterId: chapter.meta.id,
        chapterTitle: chapter.meta.title,
        label: ex.heading,
        text: truncate(ex.text, 600),
        anchor: "source",
        page: ex.page,
      });
    }
  }

  const topExamples = pickTop(chapter.examples, ex => score(`${ex.scenario} ${ex.application}`, q), MAX_EXAMPLES);
  for (const ex of topExamples) {
    sources.push({
      id: srcId(),
      kind: "example",
      chapterId: chapter.meta.id,
      chapterTitle: chapter.meta.title,
      label: truncate(ex.scenario, 70),
      text: truncate(`${ex.scenario} -> ${ex.application}`),
      anchor: "examples",
    });
  }

  const topProcedures = pickTop(chapter.procedures, p => score(`${p.title} ${p.intro ?? ""} ${p.steps.map(s => s.title).join(" ")}`, q), MAX_PROCEDURES);
  for (const p of topProcedures) {
    sources.push({
      id: srcId(),
      kind: "procedure",
      chapterId: chapter.meta.id,
      chapterTitle: chapter.meta.title,
      label: p.title,
      text: truncate(`${p.intro ?? ""} ${p.steps.map((s, i) => `${i + 1}. ${s.title}: ${s.detail}`).join(" ")}`),
      anchor: "procedures",
    });
  }

  const topComparisons = pickTop(chapter.comparisons, c => score(`${c.title} ${c.left.label} ${c.right.label} ${c.left.points.join(" ")} ${c.right.points.join(" ")}`, q), MAX_COMPARISONS);
  for (const c of topComparisons) {
    sources.push({
      id: srcId(),
      kind: "comparison",
      chapterId: chapter.meta.id,
      chapterTitle: chapter.meta.title,
      label: c.title,
      text: truncate(`${c.left.label}: ${c.left.points.join("; ")} | ${c.right.label}: ${c.right.points.join("; ")}`),
      anchor: "comparisons",
    });
  }

  const topMisconceptions = pickTop(chapter.misconceptions, m => score(`${m.myth} ${m.reality}`, q), MAX_MISCONCEPTIONS);
  for (const m of topMisconceptions) {
    sources.push({
      id: srcId(),
      kind: "misconception",
      chapterId: chapter.meta.id,
      chapterTitle: chapter.meta.title,
      label: truncate(m.myth, 60),
      text: truncate(`Myth: ${m.myth} Reality: ${m.reality}`),
      anchor: "misconceptions",
    });
  }

  // Always include overview as a low-priority safety net
  if (chapter.overview.length > 0 && sources.length < 6) {
    sources.push({
      id: srcId(),
      kind: "overview",
      chapterId: chapter.meta.id,
      chapterTitle: chapter.meta.title,
      label: "Chapter overview",
      text: truncate(chapter.overview.join(" "), 500),
      anchor: "overview",
    });
  }

  return {
    chapterId: chapter.meta.id,
    chapterTitle: chapter.meta.title,
    question,
    history,
    sources,
  };
}

export function buildLibraryContext(
  chapters: ChapterModule[],
  question: string,
  history: ChatContextPayload["history"],
): ChatContextPayload {
  nextId = 1;
  const q = tokens(question);
  const allSources: { src: ChatSource; s: number }[] = [];

  for (const c of chapters) {
    for (const g of c.glossary) {
      const s = score(`${g.term} ${g.definition}`, q) * 3;
      if (s === 0) continue;
      allSources.push({
        s,
        src: {
          id: "",
          kind: "glossary",
          chapterId: c.meta.id,
          chapterTitle: c.meta.title,
          label: g.term,
          text: truncate(g.definition),
          anchor: "glossary",
        },
      });
    }
    for (const k of c.coreConcepts) {
      const s = score(`${k.title} ${k.body.join(" ")}`, q) * 2;
      if (s === 0) continue;
      allSources.push({
        s,
        src: {
          id: "",
          kind: "concept",
          chapterId: c.meta.id,
          chapterTitle: c.meta.title,
          label: k.title,
          text: truncate(k.body.join(" ")),
          anchor: "concepts",
        },
      });
    }
    for (const ex of c.sourceExcerpts ?? []) {
      const s = score(`${ex.heading} ${ex.text}`, q) * 4;
      if (s === 0) continue;
      allSources.push({
        s,
        src: {
          id: "",
          kind: "excerpt",
          chapterId: c.meta.id,
          chapterTitle: c.meta.title,
          label: ex.heading,
          text: truncate(ex.text, 600),
          anchor: "source",
          page: ex.page,
        },
      });
    }
  }

  const top = allSources.sort((a, b) => b.s - a.s).slice(0, 14);
  for (const t of top) {
    t.src.id = srcId();
  }
  return {
    question,
    history,
    sources: top.map(t => t.src),
  };
}
